import React, { useRef, useMemo, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Stars, Line, Text } from "@react-three/drei";
import * as THREE from "three";

const GOLD      = "#FFD700";
const SAFFRON   = "#FF5500";
const GLOW      = "#FF7700";
const CYAN_GLOW = "#00E5FF";
const NAVY      = "#0B1526";
const WHITE     = "#F0F4FF";

/* ─── Shared PBR materials ─────────────────────────────────── */
const goldMat  = new THREE.MeshStandardMaterial({ color: GOLD,      metalness: 0.98, roughness: 0.06 });
const saffMat  = new THREE.MeshStandardMaterial({ color: SAFFRON,   metalness: 0.9,  roughness: 0.12 });
const cyanMat  = new THREE.MeshStandardMaterial({ color: CYAN_GLOW, metalness: 0.9,  roughness: 0.1  });
const whiteMat = new THREE.MeshStandardMaterial({ color: WHITE,     metalness: 0.7,  roughness: 0.2  });
const navyMat  = new THREE.MeshStandardMaterial({ color: "#1E3A5F", metalness: 0.8,  roughness: 0.2  });

/* ══════════════════════════════════════════════════════════════
   1. GOLD BULLION BAR — represents commodities & traded goods
══════════════════════════════════════════════════════════════ */
function BullionBar() {
  return (
    <group>
      {/* Main bar with chamfered look using nested box */}
      <mesh material={goldMat}>
        <boxGeometry args={[0.9, 0.32, 0.46]} />
      </mesh>
      <mesh material={goldMat} scale={[0.9, 1.0, 0.85]}>
        <boxGeometry args={[0.88, 0.36, 0.48]} />
      </mesh>
      {/* Engraved grooves */}
      {[-0.28, 0, 0.28].map((x, i) => (
        <mesh key={i} position={[x, 0.165, 0]} material={new THREE.MeshStandardMaterial({ color: "#B8860B", metalness: 0.9, roughness: 0.15 })}>
          <boxGeometry args={[0.04, 0.01, 0.44]} />
        </mesh>
      ))}
      {/* SEPL stamp impression */}
      <mesh position={[0, 0.165, 0]} material={new THREE.MeshStandardMaterial({ color: "#DAA520", metalness: 1, roughness: 0.05 })}>
        <boxGeometry args={[0.5, 0.01, 0.22]} />
      </mesh>
    </group>
  );
}

/* ══════════════════════════════════════════════════════════════
   2. CUSTOMS STAMP — hexagonal official seal
══════════════════════════════════════════════════════════════ */
function CustomsStamp() {
  return (
    <group>
      {/* Outer hexagonal ring */}
      <mesh material={saffMat} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.38, 0.06, 6, 6]} />
      </mesh>
      {/* Inner disc */}
      <mesh material={saffMat} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.24, 0.24, 0.06, 6]} />
      </mesh>
      {/* Star burst rays */}
      {Array.from({ length: 6 }, (_, i) => {
        const a = (i / 6) * Math.PI * 2;
        return (
          <mesh key={i}
            position={[Math.cos(a) * 0.3, 0, Math.sin(a) * 0.3]}
            rotation={[0, -a, 0]}
            material={new THREE.MeshStandardMaterial({ color: GOLD, metalness: 0.95, roughness: 0.08 })}>
            <boxGeometry args={[0.12, 0.05, 0.04]} />
          </mesh>
        );
      })}
      {/* Handle post */}
      <mesh position={[0, 0.35, 0]} material={navyMat}>
        <cylinderGeometry args={[0.06, 0.06, 0.5, 12]} />
      </mesh>
    </group>
  );
}

/* ══════════════════════════════════════════════════════════════
   3. CARGO MANIFEST SCROLL — documents / bill of lading
══════════════════════════════════════════════════════════════ */
function CargoScroll() {
  return (
    <group>
      {/* Main scroll body */}
      <mesh material={whiteMat} rotation={[0, 0, 0]}>
        <cylinderGeometry args={[0.28, 0.28, 0.7, 24]} />
      </mesh>
      {/* Top cap */}
      <mesh position={[0, 0.38, 0]} material={goldMat}>
        <cylinderGeometry args={[0.3, 0.3, 0.07, 24]} />
      </mesh>
      {/* Bottom cap */}
      <mesh position={[0, -0.38, 0]} material={goldMat}>
        <cylinderGeometry args={[0.3, 0.3, 0.07, 24]} />
      </mesh>
      {/* "Text lines" embossed on scroll */}
      {[-0.2, -0.07, 0.06, 0.19].map((y, i) => (
        <mesh key={i} position={[0.28, y, 0]} rotation={[0, 0, 0]} material={cyanMat}>
          <boxGeometry args={[0.01, 0.03, 0.3 - i * 0.04]} />
        </mesh>
      ))}
      {/* Gold seal */}
      <mesh position={[0, -0.12, 0.285]} rotation={[Math.PI / 2, 0, 0]} material={goldMat}>
        <cylinderGeometry args={[0.07, 0.07, 0.015, 16]} />
      </mesh>
    </group>
  );
}

/* ══════════════════════════════════════════════════════════════
   4. DIAMOND GEM — premium cargo, high-value exports
══════════════════════════════════════════════════════════════ */
function GemDiamond() {
  return (
    <group>
      {/* Upper pyramid */}
      <mesh position={[0, 0.18, 0]} material={cyanMat}>
        <coneGeometry args={[0.32, 0.45, 8]} />
      </mesh>
      {/* Lower pyramid (flipped) */}
      <mesh position={[0, -0.18, 0]} rotation={[Math.PI, 0, 0]} material={new THREE.MeshStandardMaterial({ color: "#00B8D9", metalness: 0.95, roughness: 0.05, transparent: true, opacity: 0.9 })}>
        <coneGeometry args={[0.32, 0.45, 8]} />
      </mesh>
      {/* Girdle ring */}
      <mesh rotation={[Math.PI / 2, Math.PI / 8, 0]} material={whiteMat}>
        <torusGeometry args={[0.32, 0.025, 8, 8]} />
      </mesh>
    </group>
  );
}

/* ══════════════════════════════════════════════════════════════
   5. GPS LOCATION PIN — global tracking & logistics
══════════════════════════════════════════════════════════════ */
function GPSPin() {
  return (
    <group>
      {/* Pin head sphere */}
      <mesh position={[0, 0.35, 0]} material={saffMat}>
        <sphereGeometry args={[0.24, 20, 20]} />
      </mesh>
      {/* Inner glow dot */}
      <mesh position={[0, 0.35, 0]} material={new THREE.MeshBasicMaterial({ color: WHITE })}>
        <sphereGeometry args={[0.1, 12, 12]} />
      </mesh>
      {/* Pin body taper */}
      <mesh position={[0, 0.05, 0]} material={saffMat}>
        <coneGeometry args={[0.15, 0.55, 20]} />
      </mesh>
      {/* Tip point */}
      <mesh position={[0, -0.32, 0]} rotation={[Math.PI, 0, 0]} material={goldMat}>
        <coneGeometry args={[0.04, 0.18, 12]} />
      </mesh>
    </group>
  );
}

/* ══════════════════════════════════════════════════════════════
   6. TRADE WEIGHT SCALE — fair trade, measurement
══════════════════════════════════════════════════════════════ */
function TradeScale() {
  return (
    <group>
      {/* Central pole */}
      <mesh material={goldMat}>
        <cylinderGeometry args={[0.035, 0.035, 0.9, 12]} />
      </mesh>
      {/* Top pivot cap */}
      <mesh position={[0, 0.45, 0]} material={goldMat}>
        <sphereGeometry args={[0.07, 12, 12]} />
      </mesh>
      {/* Horizontal beam */}
      <mesh position={[0, 0.32, 0]} material={goldMat}>
        <boxGeometry args={[0.85, 0.04, 0.04]} />
      </mesh>
      {/* Left pan */}
      <mesh position={[-0.4, 0.04, 0]} material={navyMat} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.18, 0.18, 0.035, 16]} />
      </mesh>
      {/* Right pan */}
      <mesh position={[0.4, 0.04, 0]} material={navyMat} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.18, 0.18, 0.035, 16]} />
      </mesh>
      {/* Chain lines (thin cylinders) */}
      {[[-0.4, -1], [0.4, 1]].map(([x, side], i) =>
        [0.12, 0.22].map((sx, j) => (
          <mesh key={`${i}-${j}`} position={[x + side * sx * 0.15, 0.18, 0]} material={goldMat}>
            <cylinderGeometry args={[0.008, 0.008, 0.24, 6]} />
          </mesh>
        ))
      )}
      {/* Base */}
      <mesh position={[0, -0.45, 0]} material={navyMat}>
        <boxGeometry args={[0.38, 0.06, 0.38]} />
      </mesh>
    </group>
  );
}

/* ══════════════════════════════════════════════════════════════
   7. ANCHOR — maritime heritage
══════════════════════════════════════════════════════════════ */
function Anchor() {
  return (
    <group>
      {/* Ring at top */}
      <mesh position={[0, 0.58, 0]} rotation={[Math.PI / 2, 0, 0]} material={goldMat}>
        <torusGeometry args={[0.15, 0.04, 12, 24]} />
      </mesh>
      {/* Vertical shank */}
      <mesh material={goldMat}>
        <cylinderGeometry args={[0.04, 0.04, 1.0, 12]} />
      </mesh>
      {/* Crossbar */}
      <mesh position={[0, 0.32, 0]} material={goldMat}>
        <boxGeometry args={[0.65, 0.06, 0.06]} />
      </mesh>
      {/* Left arm */}
      <mesh position={[-0.28, -0.42, 0]} rotation={[0, 0, 0.6]} material={goldMat}>
        <cylinderGeometry args={[0.04, 0.04, 0.45, 12]} />
      </mesh>
      {/* Right arm */}
      <mesh position={[0.28, -0.42, 0]} rotation={[0, 0, -0.6]} material={goldMat}>
        <cylinderGeometry args={[0.04, 0.04, 0.45, 12]} />
      </mesh>
      {/* Left fluke */}
      <mesh position={[-0.42, -0.6, 0]} rotation={[0, 0, -0.4]} material={goldMat}>
        <coneGeometry args={[0.1, 0.22, 8]} />
      </mesh>
      {/* Right fluke */}
      <mesh position={[0.42, -0.6, 0]} rotation={[0, 0, 0.4]} material={goldMat}>
        <coneGeometry args={[0.1, 0.22, 8]} />
      </mesh>
    </group>
  );
}

/* ─── Globe ─────────────────────────────────────────────────── */
function Globe() {
  const wire = useRef();
  const core = useRef();
  const atmo  = useRef();

  useFrame((_, d) => {
    wire.current.rotation.y += d * 0.12;
    core.current.rotation.y += d * 0.07;
    atmo.current.rotation.y -= d * 0.04;
  });

  return (
    <group>
      <mesh ref={core}>
        <sphereGeometry args={[2.0, 64, 64]} />
        <meshStandardMaterial color="#152A4A" metalness={0.7} roughness={0.2}
          emissive="#0A182E" emissiveIntensity={0.6} />
      </mesh>
      <mesh ref={wire} scale={1.018}>
        <icosahedronGeometry args={[2.0, 4]} />
        <meshBasicMaterial color={GOLD} wireframe transparent opacity={0.45} />
      </mesh>
      <mesh scale={1.025}>
        <sphereGeometry args={[2.0, 24, 16]} />
        <meshBasicMaterial color={CYAN_GLOW} wireframe transparent opacity={0.2} />
      </mesh>
      <mesh ref={atmo} scale={1.22}>
        <sphereGeometry args={[2.0, 32, 32]} />
        <meshBasicMaterial color={GLOW} transparent opacity={0.16} side={THREE.BackSide} />
      </mesh>
    </group>
  );
}

/* ─── Trade routes ───────────────────────────────────────────── */
function latLng(lat, lng, r = 2.03) {
  const phi   = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return [
    -r * Math.sin(phi) * Math.cos(theta),
     r * Math.cos(phi),
     r * Math.sin(phi) * Math.sin(theta),
  ];
}

function Arc({ start, end, color }) {
  const pts = useMemo(() => {
    const s   = new THREE.Vector3(...start);
    const e   = new THREE.Vector3(...end);
    const mid = s.clone().add(e).multiplyScalar(0.5).normalize().multiplyScalar(3.3);
    return new THREE.QuadraticBezierCurve3(s, mid, e).getPoints(50);
  }, [start, end]);
  return <Line points={pts} color={color} lineWidth={2.5} transparent opacity={0.9} />;
}

function TradeRoutes() {
  const india = latLng(19, 72.8);
  const dests = [
    latLng(53.5, 10), latLng(25.2, 55.3), latLng(1.3, 103.8),
    latLng(40.7, -74), latLng(35.6, 139.7), latLng(-33.8, 151.2),
  ];
  return (
    <group>
      <mesh position={india}><sphereGeometry args={[0.09, 20, 20]} /><meshBasicMaterial color={SAFFRON} /></mesh>
      <mesh position={india}><sphereGeometry args={[0.15, 16, 16]} /><meshBasicMaterial color={GOLD} transparent opacity={0.4} /></mesh>
      {dests.map((d, i) => (
        <group key={i}>
          <mesh position={d}><sphereGeometry args={[0.06, 16, 16]} /><meshBasicMaterial color={i % 2 === 0 ? GOLD : CYAN_GLOW} /></mesh>
          <Arc start={india} end={d} color={i % 2 === 0 ? GOLD : SAFFRON} />
        </group>
      ))}
    </group>
  );
}

/* ─── Individual orbiting icon with gentle bob ──────────────── */
function OrbitItem({ children, position, selfRotateSpeed = 0.5, phase = 0 }) {
  const ref = useRef();
  useFrame((state, d) => {
    if (!ref.current) return;
    ref.current.rotation.y += d * selfRotateSpeed;
    // gentle vertical bob
    ref.current.position.y = position[1] + Math.sin(state.clock.getElapsedTime() * 1.4 + phase) * 0.1;
  });
  return <group ref={ref} position={position}>{children}</group>;
}

/* ─── All 7 items in a single revolving ring ─────────────────── */
function OrbitingBusinessItems() {
  const ring = useRef();
  useFrame((_, d) => { ring.current.rotation.y += d * 0.2; });

  const SCALE = 0.5;

  return (
    <group ref={ring}>
      {/* Gold Bullion Bar — commodities trade */}
      <OrbitItem position={[4.0,  0.5,  0]}   selfRotateSpeed={0.4} phase={0}>
        <group scale={SCALE}><BullionBar /></group>
      </OrbitItem>

      {/* Customs Stamp — export clearance */}
      <OrbitItem position={[2.0,  1.2,  3.4]} selfRotateSpeed={0.6} phase={1.0}>
        <group scale={SCALE}><CustomsStamp /></group>
      </OrbitItem>

      {/* Cargo Scroll / Manifest — documentation */}
      <OrbitItem position={[-2.0, -0.8, 3.4]} selfRotateSpeed={0.5} phase={2.0}>
        <group scale={SCALE}><CargoScroll /></group>
      </OrbitItem>

      {/* Diamond Gem — premium high-value cargo */}
      <OrbitItem position={[-4.0,  0.3,  0]}  selfRotateSpeed={0.7} phase={3.0}>
        <group scale={SCALE}><GemDiamond /></group>
      </OrbitItem>

      {/* GPS Pin — global tracking */}
      <OrbitItem position={[-2.0,  1.0, -3.4]} selfRotateSpeed={0.5} phase={4.0}>
        <group scale={SCALE}><GPSPin /></group>
      </OrbitItem>

      {/* Trade Scale — fair trade & measurement */}
      <OrbitItem position={[2.0, -0.6, -3.4]}  selfRotateSpeed={0.4} phase={5.0}>
        <group scale={SCALE}><TradeScale /></group>
      </OrbitItem>

      {/* Anchor — maritime heritage */}
      <OrbitItem position={[0,  1.8, -4.0]}    selfRotateSpeed={0.55} phase={6.0}>
        <group scale={SCALE}><Anchor /></group>
      </OrbitItem>
    </group>
  );
}

/* ─── Scene ──────────────────────────────────────────────────── */
export default function Hero3D() {
  return (
    <Canvas camera={{ position: [0.8, 0.2, 5.8], fov: 45 }} dpr={[1, 2]}
      gl={{ antialias: true, alpha: true }} data-testid="hero-3d-canvas">

      <ambientLight intensity={1.5} />
      <directionalLight position={[6, 6, 6]}    intensity={3.0} color="#ffffff" />
      <directionalLight position={[-6, -4, -4]} intensity={1.8} color={GOLD} />
      <pointLight position={[-4, -2, 4]} intensity={3.8} color={SAFFRON} />
      <pointLight position={[4, 3, 5]}   intensity={3.2} color={GOLD} />
      <pointLight position={[0, 4, -3]}  intensity={2.8} color={CYAN_GLOW} />

      <Stars radius={70} depth={50} count={3500} factor={4} saturation={0.5} fade speed={1.5} />

      <Suspense fallback={null}>
        <Globe />
        <TradeRoutes />
        <OrbitingBusinessItems />
      </Suspense>

      <OrbitControls enableZoom={false} enablePan={false}
        autoRotate autoRotateSpeed={0.8}
        minPolarAngle={Math.PI / 3.5} maxPolarAngle={Math.PI / 1.6} />
    </Canvas>
  );
}
